import { useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { Link, useParams, useSearchParams } from 'react-router'
import { ContentCopy, ExpandMore } from '@mui/icons-material'
import { Accordion, AccordionDetails, AccordionSummary, Alert, Button, CircularProgress, Grid, IconButton, Tooltip, Typography } from '@mui/material'

import { getOrderIcon, getOrderTitle } from '../../../../components/orders/helpers'
import OrderField from '../../../../components/orders/order-field'
import { useHost } from '../../../../providers/app-provider'
import Api from '../../../../utils/api'
import { getFormattedDateTime } from '../../../../utils/helpers'
import useClipboard from '../../../../utils/hooks/useClipboard'
import useSharedStyles from '../../../../utils/shared.styles'

import useStyles from './index.styles'
import OrderAccordionDetails from './order-accordion-details'
import OrderChip from './order-chip'
import ReorderAccordionDetails from './reorder-accordion-details'
import ReturnAccordionDetails from './return-accordion-details'

const OrderRow = ({ orderItem }: { orderItem: IOrderListItem }) => {
  const { accountId } = useParams()
  const [, setSearchParams] = useSearchParams()
  const classes = useStyles
  const sharedClasses = useSharedStyles
  const { clickToCopy } = useClipboard()
  const auth = useAuth()
  const host = useHost()
  const [showCopy, setShowCopy] = useState<boolean>(false)
  const [expanded, setExpanded] = useState<boolean>(false)
  const [expandedDetails, setExpandedDetails] = useState<IOrderItemExpanded | IReturnItemExpanded | IReorderItemExpanded>()
  const [error, setError] = useState<string | null>(null)

  const title = getOrderTitle(orderItem.orderType)
  const icon = getOrderIcon(orderItem.orderType)

  const toggleExpand = async () => {
    if (!host || !auth.user?.access_token) return
    if (!expanded) {
      setExpanded(true)
      setError(null)
      const res = await Api.get(
        `${host}/api/v2/central/folders/${accountId}/${orderItem.orderType === 'return' || orderItem.orderType === 'scrap' ? 'returns' : orderItem.orderType === 'reorder' ? 'reorders' : 'orders'}/${
          orderItem.orderNodeId
        }/expand`
      )
      if (res.ok) {
        setExpandedDetails(res.body)
      } else {
        setError('Something went wrong and we could not load the order details')
      }
    } else {
      setExpanded(false)
    }
  }

  return (
    <Accordion variant="outlined" sx={classes.accordion} expanded={expanded} onChange={toggleExpand}>
      <AccordionSummary expandIcon={<ExpandMore />} sx={classes.accordionSummary} data-testid="accordion-header">
        <Grid container={true} size={12} sx={[sharedClasses.centerContentVertical, { p: 2, gap: 2 }]}>
          <Grid container={true} size="auto" sx={sharedClasses.centerContentVertical}>
            <img src={icon} style={classes.orderIcon} />
          </Grid>

          <OrderField label={title} value={orderItem.orderStatus ? <OrderChip label={orderItem.orderStatus} size="small" /> : '-'} />

          {/* second column */}
          {['order', 'Replacement Order', 'Battery Replacement Order'].includes(orderItem.orderType) && (
            <OrderField label="Short Order" value={orderItem.shortOrderNumber} />
          )}
          {['return', 'scrap'].includes(orderItem.orderType) && <OrderField label="RMA #" value={orderItem.rmaNumber} />}
          {['reorder'].includes(orderItem.orderType) && <OrderField label="Discount Code" value={orderItem.discountCode} />}

          {/* third column */}
          {orderItem.orderType !== 'reorder' && (
            <Grid
              container={true}
              onMouseEnter={() => setShowCopy(true)}
              onMouseLeave={() => setShowCopy(false)}
              size={3}
              sx={{
                justifyItems: 'center',
              }}
            >
              <Grid size={12}>
                <Typography variant="overline" sx={classes.summaryFieldTitle}>
                  Tracking Number
                </Typography>
              </Grid>
              <Grid container={true} size={12}>
                <Typography variant="body1" onFocus={() => setShowCopy(true)} tabIndex={orderItem.trackingNumber ? 0 : -1}>
                  {orderItem.trackingNumber && orderItem.trackingNumber.startsWith('1Z') && (
                    <Link to={`https://wwwapps.ups.com/tracking/tracking.cgi?tracknum=${orderItem.trackingNumber}`} target="_blank">
                      {orderItem.trackingNumber}
                    </Link>
                  )}
                  {orderItem.trackingNumber && !orderItem.trackingNumber.startsWith('1Z') && orderItem.trackingNumber}
                  {!orderItem.trackingNumber && '-'}
                </Typography>
                {orderItem.trackingNumber && showCopy && (
                  <Tooltip title="Copy tracking number to clipboard" arrow={true} placement="top">
                    <IconButton
                      component="span"
                      onClick={(e) => {
                        e.stopPropagation()
                        clickToCopy(orderItem.trackingNumber ?? '')
                      }}
                      onBlur={() => setShowCopy(false)}
                      sx={classes.copyIconButton}
                      aria-label="Copy number to clipboard"
                    >
                      <ContentCopy color="primary" sx={classes.copyIcon} />
                    </IconButton>
                  </Tooltip>
                )}
              </Grid>
            </Grid>
          )}

          {orderItem.orderType === 'reorder' && (
            <OrderField xs={3} label="Expiration Date" value={orderItem.expirationDate ? getFormattedDateTime(orderItem.expirationDate) : '-'} />
          )}

          {/* fourth column */}
          {orderItem.orderType !== 'reorder' && (
            <OrderField label="Last Updated" value={orderItem.updatedAt ? getFormattedDateTime(orderItem.updatedAt) : '-'} />
          )}

          {orderItem.orderType === 'reorder' && <OrderField label="Created" value={orderItem.createdAt ? getFormattedDateTime(orderItem.createdAt) : '-'} />}

          <Grid container={true} size="grow" sx={[sharedClasses.centerContentVertical, { justifyContent: 'flex-end' }]}>
            <Button
              component="a"
              onClick={(e) => {
                e.stopPropagation()
                setSearchParams({ tab: 'orders', orderId: `${orderItem.orderNodeId}` })
              }}
            >
              Details
            </Button>
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails data-testid="accordion-detail">
        {!expandedDetails && !error && (
          <Grid container={true} size={12} sx={[sharedClasses.centerContentHorizontal, classes.loadingWrapper]}>
            <CircularProgress />
          </Grid>
        )}

        {expandedDetails && !error && (
          <>
            {(orderItem.orderType === 'order' || orderItem.orderType === 'Battery Replacement Order' || orderItem.orderType === 'Replacement Order') && (
              <OrderAccordionDetails expandedDetails={expandedDetails as IOrderItemExpanded} shipToAddress={orderItem.shipToAddress} />
            )}

            {orderItem.orderType === 'reorder' && (
              <ReorderAccordionDetails expandedDetails={expandedDetails as IReorderItemExpanded} shipToAddress={orderItem.shipToAddress} />
            )}

            {(orderItem.orderType === 'return' || orderItem.orderType === 'scrap') && (
              <ReturnAccordionDetails expandedDetails={expandedDetails as IReturnItemExpanded} orderNodeId={orderItem.orderNodeId} />
            )}
          </>
        )}

        {error && <Alert severity="error">{error}</Alert>}
      </AccordionDetails>
    </Accordion>
  )
}

export default OrderRow
